import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface ProgressEntry {
  lesson_id: string;
  exercise_id: string;
  completed_at: string;
  attempts: number;
}

export function useLatexProgress() {
  const { user } = useAuth();
  const [progress, setProgress] = useState<ProgressEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProgress = useCallback(async () => {
    if (!user) {
      setProgress([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('latex_progress')
        .select('lesson_id, exercise_id, completed_at, attempts')
        .eq('user_id', user.id);

      if (error) throw error;
      setProgress(data || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const markCompleted = async (lessonId: string, exerciseId: string, attempts: number = 1) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('latex_progress')
        .upsert({
          user_id: user.id,
          lesson_id: lessonId,
          exercise_id: exerciseId,
          attempts,
          completed_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,lesson_id,exercise_id'
        });

      if (error) throw error;
      
      // Update local state
      setProgress(prev => {
        const existing = prev.find(p => p.lesson_id === lessonId && p.exercise_id === exerciseId);
        if (existing) {
          return prev.map(p => 
            p.lesson_id === lessonId && p.exercise_id === exerciseId 
              ? { ...p, attempts, completed_at: new Date().toISOString() }
              : p
          );
        }
        return [...prev, { lesson_id: lessonId, exercise_id: exerciseId, attempts, completed_at: new Date().toISOString() }];
      });
      
      return true;
    } catch (error) {
      console.error('Error marking progress:', error);
      return false;
    }
  };

  const isExerciseCompleted = (lessonId: string, exerciseId: string) => {
    return progress.some(p => p.lesson_id === lessonId && p.exercise_id === exerciseId);
  };

  const getLessonProgress = (lessonId: string, totalExercises: number) => {
    const completed = progress.filter(p => p.lesson_id === lessonId).length;
    return { completed, total: totalExercises, percentage: totalExercises > 0 ? (completed / totalExercises) * 100 : 0 };
  };

  const getModuleProgress = (lessonIds: string[], exerciseCounts: Record<string, number>) => {
    let totalCompleted = 0;
    let totalExercises = 0;
    
    lessonIds.forEach(id => {
      const count = exerciseCounts[id] || 0;
      totalExercises += count;
      totalCompleted += progress.filter(p => p.lesson_id === id).length;
    });

    return { 
      completed: totalCompleted, 
      total: totalExercises, 
      percentage: totalExercises > 0 ? (totalCompleted / totalExercises) * 100 : 0 
    };
  };

  return {
    progress,
    isLoading,
    markCompleted,
    isExerciseCompleted,
    getLessonProgress,
    getModuleProgress,
    refetch: fetchProgress,
  };
}
